import { Project } from '@prisma/client';
import { ColumnDef } from '@tanstack/react-table';
import { AvatarGroup, Badge, Checkbox } from '@zond/ui';
import { cn } from '@zond/utils';
import * as LucideIcons from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { DataTableColumnHeader } from './data-table-column-header';
import DataTableRowActions from './data-table-row-actions';

export const columns: ColumnDef<
  Project & { UserProject: Array<{ user: { id: string; name: string; image?: string | null } }> }
>[] = [
  {
    id: 'select',
    header: ({ table }) => {
      const { t } = useTranslation('projects');
      return (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-[2px]"
        />
      );
    },
    cell: ({ row }) => {
      const { t } = useTranslation('projects');
      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('table.selectRow')}
          className="translate-y-[2px]"
        />
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => {
      const { t } = useTranslation('common');
      return <DataTableColumnHeader column={column} title={t('labels.name')} />;
    },
    cell: ({ row }) => {
      const iconName = row.original.icon;
      const Icon = iconName ? (LucideIcons[iconName as keyof typeof LucideIcons] as LucideIcon) : null;
      const { workspaceId } = useParams();

      return (
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <Link
            to={`/${workspaceId}/projects/${row.original.id}`}
            className="max-w-[200px] truncate font-medium hover:underline"
          >
            {row.getValue('name')}
          </Link>
        </div>
      );
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: ({ column }) => {
      const { t } = useTranslation('common');
      return <DataTableColumnHeader column={column} title={t('labels.description')} />;
    },
    cell: ({ row }) => {
      return <div className="max-w-[500px] truncate text-sm">{row.getValue('description') || '—'}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'members',
    header: ({ column }) => {
      const { t } = useTranslation('projects');
      return <DataTableColumnHeader column={column} title={t('columns.members')} />;
    },
    cell: ({ row }) => {
      const members = row.original.UserProject.map((up) => up.user);
      return members.length ? <AvatarGroup users={members} limit={3} /> : <div className="text-sm">—</div>;
    },
    enableSorting: false,
    enableHiding: true,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: ({ column }) => {
      const { t } = useTranslation('projects');
      return <DataTableColumnHeader column={column} title={t('columns.created')} />;
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue('createdAt'));
      return <div className="flex items-center text-sm">{date.toLocaleDateString()}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
